// Phase 10A.1 R0/R1 + Phase 2 P2-7 — Audit / Public Track Record page.
//
// Two layers, top-to-bottom:
//   1. Public Track Record (Phase 2): platform-wide 30-day metrics +
//      per-formula track record + immutable desk_audit_ledger feed.
//      All three sources are append-only and seal nightly via the
//      midnight cron. This is the user-facing trust surface — what
//      makes STRIKZA's claims verifiable.
//
//   2. Operations log (Phase 1A): existing audit_events feed showing
//      agent runs, state transitions, and budget guards. Preserved
//      verbatim — the page just gains the track record sections above.

import type { CSSProperties } from "react";
import { useAuditEvents } from "../../audit/useAuditEvents.ts";
import {
  useDeskTrackRecord,
  type DeskAuditLedgerRow,
  type DeskFormulaTrackRecord,
  type DeskPlatformTrackRecord,
} from "../../audit/useDeskTrackRecord.ts";
import { Icon } from "../../components/icons/Icon.tsx";
import { AwaitingDataPanel } from "../_awaiting-data.tsx";

// ─── Static formula display copy ─────────────────────────────────

const FORMULA_TITLES: Record<DeskFormulaTrackRecord["formula_name"], string> = {
  standing_ten: "Standing Ten",
  chaos_day: "Chaos Day",
  unseen: "Unseen",
  the_anchor: "The Anchor",
  the_contrarian: "The Contrarian",
};

const FORMULA_ORDER: DeskFormulaTrackRecord["formula_name"][] = [
  "the_anchor",
  "standing_ten",
  "unseen",
  "chaos_day",
  "the_contrarian",
];

const ENTRY_KIND_LABEL: Record<DeskAuditLedgerRow["entry_kind"], string> = {
  desk_pick: "Desk Pick",
  desk_formula: "Desk Formula",
  signal_card: "Signal Card",
  spend: "Spend",
  clv_outcome: "CLV Outcome",
  correction: "Correction",
};

const ENTRY_KIND_COLOR: Record<DeskAuditLedgerRow["entry_kind"], string> = {
  desk_pick: "var(--sz-amber)",
  desk_formula: "var(--sz-info-text)",
  signal_card: "var(--sz-success-text)",
  spend: "var(--sz-text-muted)",
  clv_outcome: "var(--sz-warning-text)",
  correction: "var(--sz-danger-text)",
};

// ─── Styles ───────────────────────────────────────────────────────

const eyebrow: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

const sectionEyebrow: CSSProperties = {
  ...eyebrow,
  margin: "var(--sz-space-3) 0 var(--sz-space-2)",
};

const card: CSSProperties = {
  background: "var(--sz-bg-surface)",
  border: "1px solid var(--sz-border-default)",
  borderRadius: "var(--sz-radius-md, 8px)",
  padding: "var(--sz-space-3)",
};

const platformGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "var(--sz-space-3)",
};

const headlineMetric: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(2rem, 5vw, 2.8rem)",
  color: "var(--sz-text-primary)",
  letterSpacing: "1px",
  lineHeight: 1,
  marginTop: 6,
};

const formulaRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "180px 100px 100px 100px 100px 1fr",
  gap: "var(--sz-space-2)",
  padding: "10px 0",
  borderBottom: "1px solid var(--sz-border-subtle)",
  fontFamily: "var(--sz-font-mono)",
  fontSize: "0.85rem",
  alignItems: "baseline",
};

const ledgerRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "120px 130px 1fr 80px",
  gap: "var(--sz-space-2)",
  padding: "8px 0",
  borderBottom: "1px solid var(--sz-border-subtle)",
  fontFamily: "var(--sz-font-mono)",
  fontSize: "0.8rem",
  alignItems: "baseline",
};

// ─── Helpers ──────────────────────────────────────────────────────

const fmtPct = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(n) ? "—" : `${(n * 100).toFixed(1)}%`;

const fmtOdds = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(n) ? "—" : `${n.toFixed(2)}x`;

const fmtRelative = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

function ledgerSummary(row: DeskAuditLedgerRow): string {
  if (row.entry_kind === "desk_pick") {
    const p = row.payload as Record<string, unknown>;
    if (p.status === "abstain") return `Agent ${p.agent_id} abstained · ${p.abstain_reason ?? "no_reason"}`;
    return `Agent ${p.agent_id} · ${p.market} ${p.selection} @ ${p.odds_at_submission ?? "?"}x`;
  }
  if (row.entry_kind === "desk_formula") {
    const p = row.payload as Record<string, unknown>;
    return `${p.formula_name} · ${p.publication_status} · ${p.leg_count ?? "?"} legs @ ${p.combined_odds ?? "?"}x`;
  }
  if (row.entry_kind === "clv_outcome") {
    const p = row.payload as Record<string, unknown>;
    return `Outcome · ${p.outcome ?? "pending"} · CLV ${p.clv_result ?? "—"}`;
  }
  if (row.entry_kind === "correction") {
    const p = row.payload as Record<string, unknown>;
    return `Correction · ${p.reason ?? "no_reason"}`;
  }
  return row.entry_kind;
}

// ─── Platform headline card ──────────────────────────────────────

function PlatformHeadline({ p }: { p: DeskPlatformTrackRecord }): JSX.Element {
  return (
    <article style={card}>
      <div style={eyebrow}>Public track record · last 30 days</div>
      <div style={{ ...platformGrid, marginTop: 12 }}>
        <div>
          <span style={eyebrow}>Picks published</span>
          <div style={headlineMetric}>{p.picks_30d ?? 0}</div>
          <div style={{ ...eyebrow, marginTop: 4 }}>+ {p.abstentions_30d ?? 0} abstentions</div>
        </div>
        <div>
          <span style={eyebrow}>CLV hit rate</span>
          <div style={{ ...headlineMetric, color: "var(--sz-amber)" }}>
            {fmtPct(p.platform_clv_hit_rate_30d)}
          </div>
          <div style={{ ...eyebrow, marginTop: 4 }}>headline metric</div>
        </div>
        <div>
          <span style={eyebrow}>Win rate</span>
          <div
            style={{
              ...headlineMetric,
              color:
                p.platform_win_rate_30d && p.platform_win_rate_30d > 0.5
                  ? "var(--sz-success-text)"
                  : "var(--sz-text-primary)",
            }}
          >
            {fmtPct(p.platform_win_rate_30d)}
          </div>
          <div style={{ ...eyebrow, marginTop: 4 }}>context only</div>
        </div>
        <div>
          <span style={eyebrow}>Active agents</span>
          <div style={headlineMetric}>{p.active_agents_30d ?? 0}</div>
          <div style={{ ...eyebrow, marginTop: 4 }}>of 18 total</div>
        </div>
        <div>
          <span style={eyebrow}>Max-conviction</span>
          <div style={{ ...headlineMetric, color: "var(--sz-amber)" }}>
            {p.max_conviction_picks_30d ?? 0}
          </div>
          <div style={{ ...eyebrow, marginTop: 4 }}>30d total</div>
        </div>
      </div>
      <div
        style={{
          fontFamily: "var(--sz-font-mono)",
          fontSize: "0.7rem",
          color: "var(--sz-text-muted)",
          letterSpacing: "0.5px",
          marginTop: 12,
          fontStyle: "italic",
        }}
      >
        Win rate is shown as context only. CLV hit rate is the headline performance indicator —
        an agent that beats the closing line consistently is creating real edge.
      </div>
    </article>
  );
}

// ─── Per-formula track record table ──────────────────────────────

function FormulaTrackRecord({
  formulas,
}: {
  formulas: DeskFormulaTrackRecord[];
}): JSX.Element {
  const map = new Map(formulas.map((f) => [f.formula_name, f]));
  return (
    <article style={card}>
      <div
        style={{
          ...formulaRow,
          color: "var(--sz-text-muted)",
          fontSize: "0.7rem",
          letterSpacing: "1.5px",
          textTransform: "uppercase",
          borderBottom: "1px solid var(--sz-border-default)",
        }}
      >
        <span>Formula</span>
        <span style={{ textAlign: "right" }}>30d publ.</span>
        <span style={{ textAlign: "right" }}>30d wins</span>
        <span style={{ textAlign: "right" }}>Win rate</span>
        <span style={{ textAlign: "right" }}>Avg odds</span>
        <span style={{ textAlign: "right" }}>Legs beat CLV</span>
      </div>
      {FORMULA_ORDER.map((name) => {
        const f = map.get(name);
        if (!f) {
          return (
            <div key={name} style={{ ...formulaRow, opacity: 0.5 }}>
              <span style={{ color: "var(--sz-text-primary)" }}>{FORMULA_TITLES[name]}</span>
              <span style={{ textAlign: "right", color: "var(--sz-text-muted)" }}>0</span>
              <span style={{ textAlign: "right", color: "var(--sz-text-muted)" }}>—</span>
              <span style={{ textAlign: "right", color: "var(--sz-text-muted)" }}>—</span>
              <span style={{ textAlign: "right", color: "var(--sz-text-muted)" }}>—</span>
              <span style={{ textAlign: "right", color: "var(--sz-text-muted)" }}>—</span>
            </div>
          );
        }
        return (
          <div key={name} style={formulaRow}>
            <span style={{ color: "var(--sz-text-primary)" }}>{FORMULA_TITLES[name]}</span>
            <span style={{ textAlign: "right" }}>{f.published_30d ?? 0}</span>
            <span
              style={{
                textAlign: "right",
                color: (f.wins_30d ?? 0) > 0 ? "var(--sz-success-text)" : "var(--sz-text-muted)",
              }}
            >
              {f.wins_30d ?? "—"}
            </span>
            <span
              style={{
                textAlign: "right",
                color:
                  f.win_rate_30d && f.win_rate_30d > 0.5
                    ? "var(--sz-success-text)"
                    : "var(--sz-text-primary)",
              }}
            >
              {fmtPct(f.win_rate_30d)}
            </span>
            <span style={{ textAlign: "right", color: "var(--sz-amber)" }}>
              {fmtOdds(f.avg_odds_30d)}
            </span>
            <span style={{ textAlign: "right", color: "var(--sz-text-secondary)" }}>
              {f.total_legs_beat_clv_30d ?? 0} / {f.total_legs_settled_30d ?? 0}
            </span>
          </div>
        );
      })}
    </article>
  );
}

// ─── Audit ledger feed ────────────────────────────────────────────

function AuditLedgerFeed({ rows }: { rows: DeskAuditLedgerRow[] }): JSX.Element {
  if (rows.length === 0) {
    return (
      <article style={card}>
        <div style={{ fontFamily: "var(--sz-font-mono)", fontSize: "0.85rem", color: "var(--sz-text-muted)" }}>
          The desk_audit_ledger is empty. As THE DESK publishes daily, every pick / formula /
          outcome / correction lands here as an immutable row.
        </div>
      </article>
    );
  }
  return (
    <article style={card}>
      <div
        style={{
          ...ledgerRow,
          borderBottom: "1px solid var(--sz-border-default)",
          color: "var(--sz-text-muted)",
          fontSize: "0.7rem",
          letterSpacing: "1.5px",
          textTransform: "uppercase",
        }}
      >
        <span>When</span>
        <span>Kind</span>
        <span>Summary</span>
        <span style={{ textAlign: "right" }}>Status</span>
      </div>
      {rows.map((row) => (
        <div key={row.id} style={ledgerRow}>
          <span style={{ color: "var(--sz-text-secondary)" }}>{fmtRelative(row.created_at)}</span>
          <span style={{ color: ENTRY_KIND_COLOR[row.entry_kind] }}>
            {ENTRY_KIND_LABEL[row.entry_kind]}
          </span>
          <span style={{ color: "var(--sz-text-primary)" }}>{ledgerSummary(row)}</span>
          <span
            style={{
              textAlign: "right",
              color: row.immutable_at ? "var(--sz-success-text)" : "var(--sz-warning-text)",
              fontSize: "0.7rem",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            {row.immutable_at ? "sealed" : "open"}
          </span>
        </div>
      ))}
    </article>
  );
}

// ─── Page ─────────────────────────────────────────────────────────

export function AuditPage(): JSX.Element {
  const { events, loading: eventsLoading, error: eventsError } = useAuditEvents();
  const {
    platform,
    formulas,
    ledger,
    loading: trLoading,
    error: trError,
  } = useDeskTrackRecord();

  return (
    <>
      <div className="ex-head">
        <div>
          <h1>Audit · Public Track Record</h1>
          <div className="sub">
            Append-only intelligence ledger — every pick, every formula, every settlement.
            Read-only. Sealed nightly.
          </div>
        </div>
        <div className="pill-row">
          <span className="tag">Service-role-only at the DB layer</span>
        </div>
      </div>

      <div className="disclosure">
        <Icon name="info" size={14} />
        <span>
          Every row here corresponds to a real event recorded by the agent pipeline. STRIKZA
          never fabricates audit entries. Once sealed at midnight GMT, rows cannot be modified —
          only appended-to with corrections.
        </span>
      </div>

      {/* ─── Phase 2 P2-7 Public Track Record ─── */}

      <div style={sectionEyebrow}>Public Track Record</div>
      {trLoading ? (
        <AwaitingDataPanel
          eyebrow="Track record"
          title="Loading…"
          body="Pulling 30-day platform metrics, per-formula performance, and recent audit-ledger entries."
        />
      ) : trError !== null ? (
        <AwaitingDataPanel eyebrow="Track record" title="Read failed." body={trError} />
      ) : (
        <>
          {platform ? (
            <PlatformHeadline p={platform} />
          ) : (
            <AwaitingDataPanel
              eyebrow="Track record"
              title="No data yet."
              body="THE DESK has not yet published a 30-day window. Track record fills as agents file picks each day at 11:00 GMT."
            />
          )}

          <div style={sectionEyebrow}>Per-formula performance</div>
          <FormulaTrackRecord formulas={formulas} />

          <div style={sectionEyebrow}>Immutable ledger feed (last 50)</div>
          <AuditLedgerFeed rows={ledger} />
        </>
      )}

      {/* ─── Phase 1A Operations Log (preserved) ─── */}

      <div style={sectionEyebrow}>Operations log</div>
      {eventsLoading ? (
        <AwaitingDataPanel
          eyebrow="Audit log"
          title="Loading…"
          body="Authenticated read pending."
        />
      ) : eventsError !== null ? (
        <AwaitingDataPanel eyebrow="Audit log" title="Audit log unavailable." body={eventsError} />
      ) : events.length === 0 ? (
        <AwaitingDataPanel
          eyebrow="Audit log"
          title="No audit events yet."
          body={
            <>
              The audit log fills as the agent pipeline runs. Empty here means no event has been
              appended for your account scope yet.
            </>
          }
        />
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {events.map((evt) => (
            <div
              key={evt.id}
              style={{
                padding: 12,
                border: "1px solid var(--sz-border-default)",
                borderRadius: 6,
                background: "var(--sz-bg-surface)",
                fontFamily: "var(--sz-font-mono)",
                fontSize: 12,
                color: "var(--sz-text-secondary)",
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                gap: 12,
                alignItems: "baseline",
              }}
            >
              <span>{evt.created_at}</span>
              <span style={{ color: "var(--sz-text-primary)" }}>
                {evt.event_kind} · {evt.status}
                {evt.agent_name !== null ? ` · ${evt.agent_name}` : ""}
              </span>
              <span>{evt.fixture_id ?? "—"}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
